// src/components/admin/EditTeamMember.js
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useData } from "../../providers/dataProvider";
import TeamTile from "../meet-the-team/TeamTile";
import ImageSelector from "../shared/ImageSelector";
import { pageMeta } from "../../config/helmetConfig";
import { updateMetaTags } from "../../utils/metaManager";

// Main Edit Team Member Component
export default function EditTeamMember() {
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    updateMetaTags(pageMeta.admin);
  }, []);

  // Use the consolidated DataContext
  const {
    teamMembers,
    teamMembersLoading,
    updateTeamMember,
    addTeamMember,
    images,
    getImageUrl,
    getImageByKey,
  } = useData();

  const [newTeamMember, setTeamMember] = useState({
    id: "",
    name: "",
    position: "",
    description: "",
    image_path: "",
    display_order: 999, // Default value
    phone_number: "",
    instagram_url: "",
    instagram_name: "",
    facebook_url: "",
    facebook_name: "",
  });

  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  const saveDisabled =
    saving ||
    saveSuccess ||
    !newTeamMember.name ||
    !newTeamMember.position ||
    !newTeamMember.description;

  // Load team member data when component mounts
  useEffect(() => {
    // Only attempt to find the team member when loading is complete
    if (id === "new") {
      return;
    }

    let foundTeamMember;
    if (!teamMembersLoading && teamMembers.length > 0) {
      foundTeamMember = teamMembers.find(
        (member) => member.id.toString() === id.toString()
      );

      if (foundTeamMember) {
        // Get the aws_location from the image_id
        const image = images.find((img) => img.id === foundTeamMember.image_id);
        const imagePath = image ? image.aws_location : "";

        // Update state with the found team member data
        setTeamMember({
          id: foundTeamMember.id,
          name: foundTeamMember.name,
          position: foundTeamMember.position,
          description: foundTeamMember.description,
          image_path: imagePath,
          display_order: foundTeamMember.display_order || 999, // Load the display_order
          phone_number: foundTeamMember.phone_number || "",
          instagram_url: foundTeamMember.instagram_url || "",
          instagram_name: foundTeamMember.instagram_name || "",
          facebook_url: foundTeamMember.facebook_url || "",
          facebook_name: foundTeamMember.facebook_name || "",
        });
      }
    }
  }, [teamMembers, teamMembersLoading, id, images]);

  // Update preview image URL when image_path changes
  useEffect(() => {
    const updatePreviewUrl = async () => {
      if (newTeamMember.image_path) {
        try {
          const url = await getImageUrl(newTeamMember.image_path);
          setPreviewImageUrl(url);
        } catch (error) {
          console.error("Error getting image URL:", error);
          setPreviewImageUrl(null);
        }
      } else {
        setPreviewImageUrl(null);
      }
    };

    updatePreviewUrl();
  }, [newTeamMember.image_path, getImageUrl]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTeamMember({
      ...newTeamMember,
      [name]: value,
    });
  };

  const handleImageChange = (imageKey) => {
    setTeamMember({
      ...newTeamMember,
      image_path: imageKey,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);

      // Get the image ID from the path
      const image = getImageByKey(newTeamMember.image_path);
      const imageId = image ? image.id : null;

      // Create the data object for the API with the correct field names
      const teamMemberData = {
        name: newTeamMember.name,
        position: newTeamMember.position,
        description: newTeamMember.description,
        image_id: imageId,
        display_order: parseInt(newTeamMember.display_order) || 999, // Include display_order
        phone_number: newTeamMember.phone_number,
        instagram_url: newTeamMember.instagram_url,
        instagram_name: newTeamMember.instagram_name,
        facebook_url: newTeamMember.facebook_url,
        facebook_name: newTeamMember.facebook_name,
      };

      if (id === "new") {
        await addTeamMember(teamMemberData);
      } else {
        await updateTeamMember(id, teamMemberData);
      }
    } catch (error) {
      console.error("Error updating team member:", error);
    } finally {
      setSaving(false);
      setSaveSuccess(true);
      setTimeout(() => {
        navigate("/admin/team");
      }, 2000);
    }
  };

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col className="d-flex justify-content-between align-items-center">
          <h1 className="text-light">
            {id === "new" ? "Add" : "Edit"} Team Member
          </h1>
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => navigate("/admin/team")}
          >
            Back to Team Members
          </Button>
        </Col>
      </Row>
      {saveSuccess && (
        <Row className="mb-4">
          <Col>
            <Alert variant="success">
              Team member updated successfully! Redirecting...
            </Alert>
          </Col>
        </Row>
      )}

      <Form onSubmit={handleSubmit}>
        <Row>
          {/* Edit Form Section */}
          <Col md={12} className="mb-4">
            <Card
              className="bg-dark text-white border-secondary"
              style={{ width: "100%" }}
            >
              <Card.Header className="bg-dark text-white">
                Team Member Details
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={newTeamMember.name}
                    onChange={handleInputChange}
                    required
                    className="bg-dark text-light border-secondary"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Position</Form.Label>
                  <Form.Control
                    type="text"
                    name="position"
                    value={newTeamMember.position}
                    onChange={handleInputChange}
                    required
                    className="bg-dark text-light border-secondary"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone_number"
                    value={newTeamMember.phone_number}
                    onChange={handleInputChange}
                    placeholder="e.g., (719) 555-1234"
                    className="bg-dark text-light border-secondary"
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Instagram Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="instagram_name"
                        value={newTeamMember.instagram_name}
                        onChange={handleInputChange}
                        placeholder="e.g., @stylist_name"
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Instagram URL</Form.Label>
                      <Form.Control
                        type="url"
                        name="instagram_url"
                        value={newTeamMember.instagram_url}
                        onChange={handleInputChange}
                        placeholder="https://instagram.com/username"
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Facebook Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="facebook_name"
                        value={newTeamMember.facebook_name}
                        onChange={handleInputChange}
                        placeholder="e.g., Stylist Name"
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Facebook URL</Form.Label>
                      <Form.Control
                        type="url"
                        name="facebook_url"
                        value={newTeamMember.facebook_url}
                        onChange={handleInputChange}
                        placeholder="https://facebook.com/username"
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group>
                  <Form.Label>
                    Display Order (lower numbers shown first)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="display_order"
                    value={newTeamMember.display_order}
                    onChange={handleInputChange}
                    className="bg-dark text-light border-secondary"
                  />
                  <Form.Text className="text-muted">
                    Team members will be displayed in ascending order (1, 2, 3,
                    etc.)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Description/Bio</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="description"
                    value={newTeamMember.description}
                    onChange={handleInputChange}
                    required
                    className="bg-dark text-light border-secondary"
                  />
                </Form.Group>

                {/* Use our new ImageSelector component */}
                <ImageSelector
                  label="Profile Image"
                  value={newTeamMember.image_path}
                  onChange={handleImageChange}
                />

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/admin/team")}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="success"
                    type="submit"
                    disabled={saveDisabled}
                  >
                    {saving ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Preview Section */}
          <Col sm={11} md={10}>
            <TeamTile
              member={{
                ...newTeamMember,
                // Pass the preview URL to the TeamTile component
                image: previewImageUrl,
              }}
              reversed={false}
            />
          </Col>
        </Row>
      </Form>
    </Container>
  );
}
